// src/pages/OrderConcluidoPage.jsx - VERSÃO SUPREMA (RELATÓRIO DE MISSÕES CUMPRIDAS)
import React, { useState, useEffect } from 'react';
import MainLayout from '../components/layout/MainLayout';
import GestaoOrderCard from '../components/cards/GestaoOrderCard';
import { OrderExecutionEngine } from '../api/OrderExecutionEngine';
import { FiCheckCircle, FiRefreshCw, FiCalendar, FiDollarSign, FiCornerUpLeft } from 'react-icons/fi';
import { toast } from 'react-hot-toast';

export default function OrderConcluidoPage() {
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filters, setFilters] = useState({
        month: 'all',
        year: new Date().getFullYear()
    });

    const loadData = async () => {
        setLoading(true);
        try {
            // 🛰️ Busca específica para CONCLUÍDOS e CANCELADOS
            const data = await OrderExecutionEngine.listarHistorico();
            const list = Array.isArray(data) ? data : [];
            
            // Filtro local por Mês e Ano
            const filtered = list.filter(o => {
                const date = new Date(o.dataConclusao || o.updatedAt || o.createdAt);
                if (date.getFullYear() !== Number(filters.year)) return false;
                if (filters.month !== 'all') {
                    if (date.getMonth() + 1 !== Number(filters.month)) return false;
                }
                return true;
            });

            setOrders(filtered);
        } catch (err) {
            console.error("💥 Erro no uplink do arquivo morto.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { loadData(); }, [filters]);

    const handleRevive = async (orderId) => {
        if (!window.confirm("Deseja realmente retornar este pedido para a Triagem/Novos?")) return;
        setLoading(true);
        // Retorna para o Limbo (PENDENTE sem subcategoria) para cair na Triagem
        const success = await OrderExecutionEngine.atualizarStatus(orderId, 'PENDENTE', 'Resgatado do arquivo morto e retornado à Triagem.', 'LINHAS_MOVEIS', null, null);
        if (success) {
            toast.success("PEDIDO RETORNADO À TRIAGEM");
            loadData();
        } else {
            toast.error("FALHA AO RETORNAR PEDIDO");
            setLoading(false);
        }
    };

    // 💰 Cálculo de Receita Realizada (O ajuste sutil que faltava)
    const concluídos = orders.filter(o => o.status === 'CONCLUIDO');
    const cancelados = orders.filter(o => o.status === 'CANCELADO');

    const totalRevenue = concluídos.reduce((acc, order) => {
        const value = order.itens?.reduce((sub, it) => sub + (Number(it.quantidade) * Number(it.precoUnitario)), 0) || 0;
        return acc + value;
    }, 0);

    return (
        <MainLayout>
            <div className="max-w-[1800px] mx-auto p-8 md:p-12 space-y-12">
                
                {/* HUD DE ARQUIVO */}
                <header className="flex justify-between items-center border-b pb-12 border-[var(--border-color)]">
                    <div>
                        <h1 className="text-7xl font-black uppercase italic tracking-tighter">
                            Missões <span className="text-emerald-500">Cumpridas</span>
                        </h1>
                        <p className="text-[10px] uppercase font-bold tracking-[0.5em] opacity-30 mt-4 italic">Arquivo Histórico e Faturamento Realizado</p>
                    </div>

                    <div className="flex items-center gap-6">
                        {/* KPI DE FATURAMENTO DO MÊS */}
                        <div className="px-8 py-4 bg-emerald-500/10 border border-emerald-500/20 rounded-[2rem] text-right">
                            <span className="text-[8px] font-black uppercase opacity-40 block text-emerald-500">Receita Outorgada</span>
                            <span className="text-3xl font-black text-emerald-500">
                                {totalRevenue.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                            </span>
                        </div>
                        
                        <button onClick={loadData} className="p-7 bg-[var(--bg-card)] border border-[var(--border-color)] text-emerald-500 rounded-[2rem] hover:rotate-180 transition-all duration-700">
                            <FiRefreshCw size={28} className={loading ? 'animate-spin' : ''} />
                        </button>
                    </div>
                </header>

                {/* BARRA DE FILTRAGEM TÁTICA */}
                <div className="flex gap-4 p-4 bg-[var(--bg-card)] rounded-3xl border border-[var(--border-color)] w-fit">
                    <div className="flex items-center gap-2 px-4 border-r border-[var(--border-color)]">
                        <FiCalendar className="text-accent" />
                        <select 
                            value={filters.month} 
                            onChange={(e) => setFilters({...filters, month: e.target.value})}
                            className="bg-transparent font-black uppercase text-[11px] cursor-pointer outline-none"
                        >
                            <option value="all">TODOS OS MESES</option>
                            {Array.from({length: 12}, (_, i) => (
                                <option key={i+1} value={i+1}>{new Date(0, i).toLocaleString('pt-BR', {month: 'long'})}</option>
                            ))}
                        </select>
                    </div>
                    <div className="flex items-center gap-2 px-4">
                        <select 
                            value={filters.year} 
                            onChange={(e) => setFilters({...filters, year: e.target.value})}
                            className="bg-transparent font-black uppercase text-[11px] cursor-pointer"
                        >
                            {[2024, 2025, 2026].map(y => <option key={y} value={y}>{y}</option>)}
                        </select>
                    </div>
                </div>

                {/* GRID DE RESULTADOS */}
                <div className="space-y-20 pb-40">
                    
                    {/* SESSÃO CONCLUÍDOS (VERDE) */}
                    <div>
                        <div className="flex items-center gap-4 mb-8">
                            <div className="w-4 h-4 rounded-full bg-emerald-500 animate-pulse shadow-[0_0_15px_rgba(16,185,129,0.5)]"></div>
                            <h2 className="text-2xl font-black uppercase tracking-widest text-emerald-500">
                                Missões de Sucesso ({concluídos.length})
                            </h2>
                        </div>
                        
                        {concluídos.length > 0 ? (
                            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-12">
                                {concluídos.map(order => (
                                    <div key={order._id} className="relative rounded-3xl overflow-hidden border border-emerald-500/30">
                                        <div className="absolute inset-0 bg-emerald-500/5 pointer-events-none"></div>
                                        <GestaoOrderCard order={order} readOnly={true} />
                                        <div className="p-4 border-t border-emerald-500/20 bg-emerald-500/5 flex justify-end">
                                            <button onClick={() => handleRevive(order._id)} className="flex items-center gap-2 px-4 py-2 bg-black/40 hover:bg-emerald-600 border border-emerald-600/50 rounded-xl text-[10px] font-black uppercase text-emerald-500 hover:text-white transition-all">
                                                <FiCornerUpLeft size={14} /> Retornar p/ Triagem
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="py-12 border border-emerald-500/20 rounded-3xl bg-emerald-500/5 text-emerald-500/40 font-black uppercase tracking-widest flex items-center justify-center">
                                Nenhum sucesso registrado
                            </div>
                        )}
                    </div>

                    {/* SESSÃO CANCELADOS (VERMELHO) */}
                    <div>
                        <div className="flex items-center gap-4 mb-8">
                            <div className="w-4 h-4 rounded-full bg-red-500 shadow-[0_0_15px_rgba(239,68,68,0.5)]"></div>
                            <h2 className="text-2xl font-black uppercase tracking-widest text-red-500">
                                Missões Abortadas ({cancelados.length})
                            </h2>
                        </div>
                        
                        {cancelados.length > 0 ? (
                            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-12">
                                {cancelados.map(order => (
                                    <div key={order._id} className="relative rounded-3xl overflow-hidden border border-red-500/30 grayscale hover:grayscale-0 transition-all duration-500">
                                        <div className="absolute inset-0 bg-red-500/5 pointer-events-none"></div>
                                        <GestaoOrderCard order={order} readOnly={true} />
                                        <div className="p-4 border-t border-red-500/20 bg-red-500/5 flex justify-end">
                                            <button onClick={() => handleRevive(order._id)} className="flex items-center gap-2 px-4 py-2 bg-black/40 hover:bg-red-600 border border-red-600/50 rounded-xl text-[10px] font-black uppercase text-red-500 hover:text-white transition-all">
                                                <FiCornerUpLeft size={14} /> Retornar p/ Triagem
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="py-12 border border-red-500/20 rounded-3xl bg-red-500/5 text-red-500/40 font-black uppercase tracking-widest flex items-center justify-center">
                                Sem registros de aborto
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </MainLayout>
    );
}